// Whether this page load is someone arriving from a "redefinir senha" e-mail.
//
// Getting this wrong in either direction is expensive, so it is worth being
// explicit about why there are two independent signals for one fact.
//
// supabase-js tells us through an `onAuthStateChange` event, `PASSWORD_RECOVERY`
// — but it fires that inside a `setTimeout(..., 0)` scheduled during the
// client's own initialization, which runs at module load. `AuthProvider`
// subscribes from a `useEffect`, which runs later. When the timeout wins that
// race the event lands with nobody listening, and the session it carries is
// already saved: the visitor drops straight into the app, signed in, with the
// password they came here to change still in place.
//
// The URL is the other signal, and it has the opposite weakness. The tokens ride
// in the hash (`flowType` defaults to `'implicit'` and this client does not
// override it), and supabase-js clears that hash once it has exchanged them —
// after an awaited network round-trip, but it does clear it.
//
// So: neither signal alone is safe, and they fail at different moments. This
// module owns the URL half, snapshotting the address at import time — before
// any `await` inside the client's initialization can resolve and scrub it — and
// `AuthContext` ORs it with the event.
//
// Pure on purpose, like `pre-auth-route`: the decision is a function of a URL
// string, so it is testable without a browser.

/** The `type` Supabase stamps on a recovery callback, in hash or query. */
const RECOVERY_TYPE = 'recovery';

/**
 * True when `href` is Supabase returning someone from a password-reset link.
 *
 * Requires a `type=recovery` AND no `error` alongside it: an expired or reused
 * link comes back as `#error=access_denied&error_code=otp_expired` with no
 * session, and demanding a new password there would render a field that cannot
 * possibly save. `pre-auth-route` already sends that case to the login form.
 */
export function carriesPasswordRecovery(href: string): boolean {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return false;
  }

  const hash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
  const fromHash = new URLSearchParams(hash);

  // An error in either place means no session was created.
  if (url.searchParams.has('error') || fromHash.has('error')) return false;

  return (
    url.searchParams.get('type') === RECOVERY_TYPE || fromHash.get('type') === RECOVERY_TYPE
  );
}

/**
 * The address this tab opened on, captured at import time.
 *
 * Read once and frozen deliberately — see the module docblock. Reading
 * `window.location.href` later would be reading it after supabase-js has had a
 * chance to blank the hash.
 */
const ENTRY_HREF = typeof window === 'undefined' ? '' : window.location.href;

/**
 * Survives a reload of the new-password screen.
 *
 * Both live signals are single-shot: the hash is gone once supabase-js has
 * exchanged it, and the event fires once. So a refresh — or a crashed tab
 * reopened — would find a valid session, no recovery marker anywhere, and let
 * the visitor into the app with the password still unchanged. That is the same
 * defect this flow exists to close, reached through a different door.
 *
 * `sessionStorage`, not `localStorage`, on purpose: the obligation belongs to
 * the tab that opened the link. Signing in normally somewhere else later is not
 * a recovery and must not be interrupted by one.
 */
const RECOVERY_KEY = 'crie3.password-recovery';

/** Storage throws in private mode and when site data is blocked; never fatal. */
function readFlag(): boolean {
  try {
    return window.sessionStorage.getItem(RECOVERY_KEY) === '1';
  } catch {
    return false;
  }
}

/** Remembers that this tab still owes a new password. */
export function rememberPasswordRecovery(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(RECOVERY_KEY, '1');
  } catch {
    // Falls back to the in-memory flag, which still covers the common path.
  }
}

/** Called once the password is actually written, and on sign-out. */
export function forgetPasswordRecovery(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(RECOVERY_KEY);
  } catch {
    // Nothing to undo.
  }
}

/** Browser entry point; `false` during static export, where there is no URL. */
export function enteredOnPasswordRecovery(): boolean {
  if (typeof window === 'undefined') return false;
  return carriesPasswordRecovery(ENTRY_HREF) || readFlag();
}
